/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useContext, useEffect, useState } from 'react';
import type {
  ChangeEvent,
  CSSProperties,
  Dispatch,
  ReactNode,
  SetStateAction
} from 'react';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Drawer from '@mui/material/Drawer';
import Badge from '@mui/material/Badge';
import { useForm } from 'react-hook-form';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useTranslation } from 'react-i18next';

import CatalogFilter, {
  type Props as CatalogFilterProps
} from 'components/shared/CatalogFilter/CatalogFilter';
import type { UseCatalogParams } from 'hooks/useCatalog';
import { useGlobalContext } from 'context/GlobalContext';
import { handleMediaQuery, hasValue } from 'helpers/utils';
import { FilterContext } from 'context/FilterContext';

import SearchInput from './SearchInput';
import AddButton from './AddButton';
import ActionButton from './ActionButton';
import SelectTextTypes, { TChildrenSelectOptions } from './SelectTextType';

const innerWidth = () => {
  if (window.innerWidth > 1045) return 410;
  if (window.innerWidth > 880) return 290;
  return false;
};

const styles = {
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    width: '100%',
    height: '92%',
    right: 0,
    left: 0,
    paddingLeft: 3,
    paddingRight: 1
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  mainContentContainer: {
    display: 'flex',
    position: 'relative',
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden'
  },
  mainContent: {
    display: 'flex',
    flex: 1,
    marginBottom: 50
  },
  childrenContainer: {
    flex: 1,
    marginRight: 8
  },
  drawer: {
    paper: {
      position: 'absolute' as const,
      top: 27,
      right: 0,
      borderLeft: 'none'
    }
  },
  catalogContainer: { marginTop: 32, marginRight: 24, marginBottom: 80 }
};

const Main = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'hasDrawer'
})<{
  open?: boolean;
  hasDrawer?: boolean;
  mr: number;
}>(({ theme, open, hasDrawer, mr }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  marginTop: theme.spacing(3),
  ...(hasDrawer && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    marginRight: -mr,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen
      }),
      marginRight: 0
    })
  })
}));

interface Props {
  catalogFilterProps?: CatalogFilterProps;
  children?: ReactNode;
  childrenActionButtons?: ReactNode;
  childrenLeftActionButtons?: ReactNode;
  hasChildrenLeftActionButtons?: boolean;
  childrenButtonNextToInputSearch?: ReactNode;
  containerCatalogStyles?: CSSProperties;
  ContainerSearchStyles?: CSSProperties;
  ContainerStyles?: CSSProperties;
  displaySearchInput?: boolean;
  drawerContent?: ReactNode;
  handleClearSearchInputValueCallback?: () => void;
  handleResetItemsFromExcelExport?: () => void;
  header?: ReactNode;
  hideActionButton?: boolean;
  SearchStyles?: CSSProperties;
  setParams: Dispatch<SetStateAction<UseCatalogParams>>;
  searchClassName?: string;
  handleResetItemsForExport?: () => void;
  marginBottomMainContent?: number;
  hasChildrenSelectOptions?: boolean;
  isDataFiltered?: boolean;
  filterBadgeCount?: number;
  childrenSelectOptionsFrom?: TChildrenSelectOptions;
  /** Permite definir el valor por defecto del selector de tipo de texto (solo para esta instancia) */
  defaultTextTypeValue?: number;
}

interface SearchFormFields {
  text: string;
  textType?: number;
}

function CatalogLayout({
  catalogFilterProps,
  children,
  childrenActionButtons,
  childrenLeftActionButtons,
  hasChildrenLeftActionButtons = false,
  childrenButtonNextToInputSearch,
  containerCatalogStyles,
  ContainerSearchStyles,
  ContainerStyles,
  displaySearchInput = true,
  drawerContent,
  handleClearSearchInputValueCallback,
  handleResetItemsFromExcelExport,
  handleResetItemsForExport,
  header,
  hideActionButton = false,
  SearchStyles,
  searchClassName = '',
  hasChildrenSelectOptions,
  childrenSelectOptionsFrom,
  setParams,
  isDataFiltered = true,
  filterBadgeCount = 0,
  defaultTextTypeValue
}: Props) {
  const { t } = useTranslation('global');

  const [isDesktop, setIsDesktop] = useState<number | boolean>(innerWidth());
  const [isMobileFilter, setIsMobileFilter] = useState<boolean>(
    window.innerWidth < 350
  );

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [textTypeValue, setTextTypeValue] = useState(defaultTextTypeValue ?? 1);

  const {
    registerFindData,
    onSubmit,
    setOpenModal,
    hideAddButton,
    title,
    form,
    renderInputUser,
    onPageChange,
    reset
  } = catalogFilterProps || {};

  const { showApiAlert } = useGlobalContext();

  const {
    storedFilter,
    currentLocation,
    storedTextTypeSearch,
    setStoredTextTypeSearch
  } = useContext(FilterContext);

  const toggleDrawer = useCallback(() => {
    setIsDrawerOpen((prevState) => !prevState);
  }, [setIsDrawerOpen]);

  const searchForm = useForm<SearchFormFields>({
    mode: 'onBlur'
  });

  const totalCount = registerFindData || 0;

  useEffect(() => {
    if (!storedTextTypeSearch.current) return;
    if (storedTextTypeSearch.current[currentLocation])
      setTextTypeValue(storedTextTypeSearch.current[currentLocation]);
  }, []);

  const handleSubmitSearchInput = useCallback(
    (values: SearchFormFields) => {
      const { text } = values;

      if (!hasValue({ text })) {
        showApiAlert(null, t('common.commonMessages.search'));
        return;
      }

      if (handleResetItemsFromExcelExport) handleResetItemsFromExcelExport();
      if (handleResetItemsForExport) handleResetItemsForExport();

      if (onPageChange) onPageChange(0);
      setParams((prevParams) => ({
        ...prevParams,
        ...(form && form.getValues()),
        ...(textTypeValue && text ? { textType: textTypeValue } : {}),
        search: text,
        text,
        Text: text
      }));
    },
    [
      handleResetItemsFromExcelExport,
      handleResetItemsForExport,
      onPageChange,
      setParams,
      form,
      textTypeValue
    ]
  );

  const handleSubmit = useCallback(
    (values: SearchFormFields) => {
      const searchValues =
        (searchForm.getValues('text') || values.text) === ''
          ? undefined
          : searchForm.getValues('text') || values.text;
      const cloneValuesWithoutText: {
        [key: string]: string | number | undefined;
      } = {
        ...values,
        text: undefined,
        hasAdvancedSearch: undefined
      };

      if (!searchValues && !hasValue(cloneValuesWithoutText)) {
        showApiAlert(null, t('common.commonMessages.search'));
        return;
      }

      if (handleResetItemsFromExcelExport) handleResetItemsFromExcelExport();
      if (handleResetItemsForExport) handleResetItemsForExport();
      if (onPageChange) onPageChange(0);

      const paramsSubmit = {
        ...values,
        search: searchValues,
        text: searchValues
      };

      if (textTypeValue && hasChildrenSelectOptions) {
        paramsSubmit.textType = textTypeValue;
      }
      onSubmit(paramsSubmit);
    },
    [
      searchForm,
      textTypeValue,
      handleResetItemsFromExcelExport,
      handleResetItemsForExport,
      onPageChange,
      onSubmit,
      showApiAlert
    ]
  );

  const handleClearSearchValue = useCallback(() => {
    if (form) {
      if (reset) reset();
      form.reset({
        text: '',
        search: ''
      });
    } else {
      searchForm.setValue('text', '');

      setParams((prevParams) => ({
        ...prevParams,
        text: '',
        search: '',
        Text: ''
      }));
    }

    if (onPageChange) onPageChange(0);

    if (handleClearSearchInputValueCallback) {
      handleClearSearchInputValueCallback();
    }
    if (handleResetItemsFromExcelExport) handleResetItemsFromExcelExport();
    if (handleResetItemsForExport) handleResetItemsForExport();
  }, [
    form,
    handleClearSearchInputValueCallback,
    handleResetItemsFromExcelExport,
    handleResetItemsForExport,
    searchForm,
    setParams,
    onPageChange
  ]);

  const handleOpenCreateModal = useCallback(() => {
    setOpenModal(true);
  }, [setOpenModal]);

  const handleTextType = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      setTextTypeValue(Number(target?.value));
      setStoredTextTypeSearch(Number(target?.value));
    },
    []
  );

  /** handle media queries  */
  handleMediaQuery('(min-width: 351px)', () => setIsMobileFilter(false));
  handleMediaQuery('(max-width: 350px)', () => setIsMobileFilter(true));
  handleMediaQuery('(min-width: 1046px)', () => setIsDesktop(410));
  handleMediaQuery('(max-width: 880px)', () => setIsDesktop(false));
  handleMediaQuery('(min-width: 881px) and (max-width: 1045px)', () =>
    setIsDesktop(300)
  );

  const drawerWidth = typeof isDesktop === 'number' ? isDesktop : 240;
  const storedFilterMsg = () => {
    if (storedFilter) {
      if (storedFilter[currentLocation] && isDataFiltered)
        return `- ${t('common.commonMessages.dataFiltered')}`;
      return '';
    }
    return '';
  };
  return (
    <Box sx={ContainerStyles || styles.container}>
      <div style={ContainerSearchStyles || null}>
        <div
          className={`searchAndAddButtonContainer ${searchClassName}`}
          style={SearchStyles}
        >
          {hasChildrenLeftActionButtons && (
            <ActionButton>{childrenLeftActionButtons}</ActionButton>
          )}

          {displaySearchInput && (
            <SearchInput
              childrenActionButton={childrenButtonNextToInputSearch}
              childrenSelectOptions={
                hasChildrenSelectOptions ? (
                  <SelectTextTypes
                    onChangeTextType={handleTextType}
                    type={childrenSelectOptionsFrom}
                    value={textTypeValue}
                  />
                ) : null
              }
              form={catalogFilterProps.form || searchForm}
              handleClearSearchValue={handleClearSearchValue}
              onSubmit={form ? handleSubmit : handleSubmitSearchInput}
            />
          )}

          {hideActionButton && (
            <ActionButton>{childrenActionButtons}</ActionButton>
          )}

          {!hideAddButton && (
            <AddButton
              disabled={false}
              onClickAddButton={handleOpenCreateModal}
              title={title}
            />
          )}
        </div>

        {header}

        {!catalogFilterProps.hideCount && (
          <div style={styles.headerContent}>
            <Typography fontWeight={500}>{`${t(
              'common.commonProperties.logsFound'
            )?.replace('{{number}}', `(${totalCount})`)}
              ${storedFilterMsg()}`}</Typography>
            {renderInputUser && renderInputUser}
          </div>
        )}
      </div>

      <Box sx={styles.mainContentContainer}>
        <Main hasDrawer={!!drawerContent} mr={drawerWidth} open={isDrawerOpen}>
          <div
            style={{
              ...styles.mainContent,
              ...(isMobileFilter
                ? {
                    flexDirection: 'column-reverse',
                    marginRight: '.4em',
                    marginTop: '-1em'
                  }
                : {})
            }}
          >
            <div
              style={{
                ...styles.childrenContainer,
                marginRight: isDesktop ? 8 : 1
              }}
            >
              {children}
            </div>

            {drawerContent && (!isDrawerOpen || isDesktop) && (
              <div
                style={{
                  marginLeft: 0,
                  ...(isMobileFilter
                    ? { display: 'flex', justifyContent: 'flex-end' }
                    : {})
                }}
              >
                <Tooltip
                  sx={{ padding: { xs: '0px', sm: '8px' } }}
                  title={t('common.commonProperties.filters')}
                >
                  <IconButton
                    aria-label="open drawer"
                    color="inherit"
                    onClick={toggleDrawer}
                  >
                    <Badge
                      badgeContent={filterBadgeCount}
                      color="error"
                      invisible={!filterBadgeCount}
                    >
                      <FilterAltIcon
                        sx={{
                          color:
                            storedFilter &&
                            storedFilter[currentLocation] &&
                            isDataFiltered
                              ? 'red'
                              : 'none'
                        }}
                      />
                    </Badge>
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </div>
        </Main>

        {drawerContent && (
          <Drawer
            anchor="right"
            open={isDrawerOpen}
            PaperProps={{
              style: {
                ...styles.drawer.paper,
                ...(isMobileFilter && { top: 0 })
              }
            }}
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                maxHeight: {
                  xs: 'auto',
                  sm: '95%'
                },
                height: {
                  xs: '100vh',
                  sm: 'auto'
                }
              }
            }}
            variant="persistent"
          >
            <Typography fontWeight={500} style={{ marginTop: 6 }}>
              {!isDesktop && (
                <IconButton
                  aria-label="open drawer"
                  color="inherit"
                  onClick={toggleDrawer}
                  size="small"
                >
                  <FilterAltIcon fontSize="small" />
                </IconButton>
              )}
              {t('common.commonProperties.filters')}
            </Typography>

            <div
              className="filterResponsiveView"
              style={
                containerCatalogStyles || {
                  ...styles.catalogContainer,
                  marginLeft: !isDesktop ? '1.5rem' : ''
                }
              }
            >
              <CatalogFilter
                form={form || searchForm}
                handleResetItemsFromExcelExport={
                  handleResetItemsForExport || handleResetItemsFromExcelExport
                }
                hideSearchButton={false}
                onSubmit={handleSubmit}
                renderInputUser={false}
                textType={hasChildrenSelectOptions ? textTypeValue : null}
                clearButton
                hideAddButton
                hideSearchInput
                hideStatus
                isFromRefactor
                {...catalogFilterProps}
              >
                {drawerContent}
              </CatalogFilter>
            </div>
          </Drawer>
        )}
      </Box>
    </Box>
  );
}

export default CatalogLayout;
